/**
 * Repurpose AI Service
 * 
 * Frontend service for AI content generation through the agent.
 * Simplified: No streaming - simple request/response pattern.
 * 
 * All generation flows through the agent endpoint.
 */

import { getAuth } from 'firebase/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

/**
 * Get auth headers for authenticated API requests.
 * @returns {Promise<object>}
 */
const getAuthHeaders = async () => {
    try {
        const auth = getAuth();
        const token = await auth.currentUser?.getIdToken();
        return {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        };
    } catch {
        return { 'Content-Type': 'application/json' };
    }
};

/**
 * Assemble context for AI generation
 * @param {object} params - Context parameters
 * @param {string} params.currentPost - The current generated post for refinement
 * @returns {object} - Assembled context object
 */
export const assembleContext = ({
    episode,
    planContent,
    userInstructions = {},
    currentPost = null
}) => {
    return {
        episodeId: episode?.id || null,
        guestName: episode?.guestName || episode?.founderName ||
            (episode?.firstName && episode?.lastName ?
                `${episode.firstName} ${episode.lastName}` : null),
        guestBio: episode?.guestBio || null,
        topics: episode?.topics || null,
        planContent: planContent || null,
        transcript: episode?.transcript?.text || episode?.transcript || null,
        userInstructions,
        currentPost
    };
};

class RepurposeAiService {
    constructor() {
        this.abortController = null;
    }

    /**
     * Cancel any ongoing request
     */
    cancel() {
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
    }

    /**
     * Send a message to the agent
     * 
     * @param {object} params
     * @param {string} params.episodeId - Episode ID
     * @param {string} params.message - User's message
     * @param {string} params.platform - Platform (linkedin, twitter, facebook, instagram)
     * @param {string} params.contentType - Content type (post, reels, coming_soon)
     * @param {object} params.context - Episode context
     * @param {string} params.conversationId - Existing conversation ID (optional)
     * @returns {Promise<object>} Agent response
     */
    async chat({ episodeId, message, platform, contentType, context, conversationId, onToken, onToolStart, onToolComplete }) {
        this.cancel();
        this.abortController = new AbortController();

        const headers = await getAuthHeaders();

        try {
            const response = await fetch(`${API_BASE_URL}/api/agent/chat`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    episodeId: episodeId || 'default',
                    message,
                    platform: platform || 'linkedin',
                    contentType: contentType || 'post',
                    context: context || {},
                    conversationId,
                    stream: true
                }),
                signal: this.abortController.signal
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let parsedResult = null;

            while (true) {
                const { done, value } = await reader.read();

                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                let currentEvent = '';

                for (const line of lines) {
                    if (line.startsWith('event: ')) {
                        currentEvent = line.slice(7).trim();
                    } else if (line.startsWith('data: ')) {
                        let data;
                        try {
                            data = JSON.parse(line.slice(6).trim());
                        } catch (e) {
                            continue;
                        }

                        if (!data) continue;

                        switch (currentEvent) {
                            case 'tool_start':
                                if (onToolStart) onToolStart(data);
                                break;
                            case 'tool_complete':
                                if (onToolComplete) onToolComplete(data);
                                break;
                            case 'content_token':
                                if (onToken) onToken(data.token);
                                break;
                            case 'complete':
                                parsedResult = data;
                                break;
                            case 'error':
                                throw new Error(data.error || 'Unknown error');
                        }
                    }
                }
            }

            return parsedResult || { success: false, error: 'No result returned from stream' };

        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('Request aborted by user');
                return { success: false, error: 'Request aborted' };
            }
            throw error;
        } finally {
            this.abortController = null;
        }
    }

    /**
     * Generate content (convenience method)
     * 
     * @param {object} params
     * @param {string} params.episodeId - Episode ID
     * @param {string} params.platform - Platform
     * @param {object} params.context - Episode context
     * @param {string} params.userRequest - What the user wants
     * @returns {Promise<object>} Generated content
     */
    async generateContent({ episodeId, platform, context, userRequest, onToken, onToolStart, onToolComplete }) {
        const result = await this.chat({
            episodeId,
            message: userRequest,
            platform,
            contentType: 'post',
            context,
            onToken,
            onToolStart,
            onToolComplete
        });

        return {
            success: result.success,
            content: result.generatedContent,
            response: result.response,
            conversationId: result.conversationId
        };
    }

    /**
     * Modify existing content
     * 
     * @param {object} params
     * @param {string} params.episodeId - Episode ID
     * @param {string} params.platform - Platform
     * @param {object} params.context - Episode context (must include currentContent)
     * @param {string} params.instruction - Modification instruction
     * @returns {Promise<object>} Modified content
     */
    async modifyContent({ episodeId, platform, context, instruction, onToken, onToolStart, onToolComplete }) {
        const result = await this.chat({
            episodeId,
            message: instruction,
            platform,
            contentType: 'post',
            context: {
                ...context,
                currentContent: context.currentContent
            },
            onToken,
            onToolStart,
            onToolComplete
        });

        return {
            success: result.success,
            content: result.generatedContent,
            response: result.response,
            conversationId: result.conversationId
        };
    }

    /**
     * Orchestrate - backward compatibility method
     * Routes to the agent chat endpoint
     * 
     * @param {object} params
     * @param {string} params.message - User's message
     * @param {string} params.platform - Platform
     * @param {string} params.contentType - Content type
     * @param {object} params.context - Episode context
     * @returns {Promise<object>} Orchestration result
     */
    async orchestrate({ message, platform = 'linkedin', contentType = 'post', context = {}, onToken, onToolStart, onToolComplete }) {
        const result = await this.chat({
            episodeId: context.episodeId || 'default',
            message,
            platform,
            contentType,
            context,
            onToken,
            onToolStart,
            onToolComplete
        });

        // Map to old format for backward compatibility
        const toolName = result.executedTools?.[0]?.name || 'conversational';
        const mappedTool = this._mapToolName(toolName);

        return {
            success: result.success,
            tool: mappedTool,
            parameters: {
                response: result.response,
                ...result.executedTools?.[0]?.parameters
            },
            action: {
                type: this._mapToolToActionType(mappedTool),
                response: result.response,
                platform: result.executedTools?.[0]?.parameters?.platform || platform,
                generatedContent: result.generatedContent
            }
        };
    }

    /**
     * Map new tool names to old format for backward compatibility
     * @private
     */
    _mapToolName(toolName) {
        const mapping = {
            'generate_post': 'content_generation',
            'modify_post': 'content_edit',
            'generate_image': 'coming_soon_poster',
            'modify_image': 'image_edit',
            'generate_caption': 'content_generation',
            'modify_caption': 'content_edit',
            'conversational': 'conversational'
        };
        return mapping[toolName] || toolName;
    }

    /**
     * Map tool name to action type
     * @private
     */
    _mapToolToActionType(toolName) {
        const mapping = {
            'content_generation': 'generate',
            'content_edit': 'edit',
            'coming_soon_poster': 'poster',
            'image_edit': 'image_edit',
            'conversational': 'conversation'
        };
        return mapping[toolName] || 'conversation';
    }

    /**
     * Check AI service health
     * @returns {Promise<object>}
     */
    async checkHealth() {
        try {
            const response = await fetch(`${API_BASE_URL}/api/agent/health`);
            return await response.json();
        } catch (error) {
            console.error('Health check failed:', error);
            return { status: 'error', error: error.message };
        }
    }

    // ============ LEGACY METHODS FOR BACKWARD COMPATIBILITY ============

    /**
     * Stream generation - legacy method, now calls chat without streaming
     * @deprecated Use chat() instead
     */
    async streamGeneration({ prompt, context, conversationHistory = [], onToken, onComplete, onError }) {
        try {
            const result = await this.chat({
                episodeId: context?.episodeId || 'default',
                message: prompt,
                platform: context?.platform || 'linkedin',
                contentType: 'post',
                context,
                onToken
            });

            if (onComplete) {
                onComplete({
                    content: result.generatedContent,
                    response: result.response
                });
            }

            return result;
        } catch (error) {
            if (onError) onError(error);
            throw error;
        }
    }

    /**
     * Stream LinkedIn generation - legacy method
     * @deprecated Use chat() instead
     */
    async streamLinkedInGeneration({ prompt, context, platform = 'linkedin', onToken, onComplete, onError }) {
        return this.streamGeneration({
            prompt,
            context: { ...context, platform },
            onToken,
            onComplete,
            onError
        });
    }

    /**
     * Stream edit - legacy method
     * @deprecated Use modifyContent() instead
     */
    async streamEdit({ sourceText, instruction, context, platform, onToken, onComplete, onError }) {
        try {
            const result = await this.chat({
                episodeId: context?.episodeId || 'default',
                message: instruction,
                platform: platform || context?.platform || 'linkedin',
                contentType: 'post',
                context: {
                    ...context,
                    currentContent: sourceText
                },
                onToken
            });

            if (onComplete) {
                onComplete({
                    content: result.generatedContent,
                    response: result.response,
                    isEdit: true
                });
            }

            return result;
        } catch (error) {
            if (onError) onError(error);
            throw error;
        }
    }
}

// Export singleton instance
const repurposeAiService = new RepurposeAiService();
export default repurposeAiService;
